interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  totalItems?: number;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100, 200],
  onPageSizeChange,
  totalItems,
}: PaginationProps) {
  const safeTotalPages = Math.max(1, totalPages);
  const safePage = Math.min(Math.max(1, currentPage), safeTotalPages);
  const atFirst = safePage === 1;
  const atLast = safePage === safeTotalPages;

  const rangeStart =
    pageSize && totalItems !== undefined && totalItems > 0
      ? (safePage - 1) * pageSize + 1
      : null;
  const rangeEnd =
    pageSize && totalItems !== undefined
      ? Math.min(safePage * pageSize, totalItems)
      : null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-white border-t">
      <div className="text-sm text-gray-600">
        {rangeStart !== null && rangeEnd !== null && totalItems !== undefined ? (
          totalItems === 0 ? (
            <span>No results</span>
          ) : (
            <span>
              Showing <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong> of{' '}
              <strong>{totalItems}</strong>
            </span>
          )
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        {onPageSizeChange && pageSize !== undefined && (
          <label className="flex items-center gap-2 text-sm text-gray-600 mr-2">
            <span>Per page</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="px-2 py-1 text-sm border border-gray-300 rounded"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        )}

        <button
          onClick={() => onPageChange(1)}
          disabled={atFirst}
          className="px-3 py-1.5 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          First
        </button>

        <button
          onClick={() => onPageChange(safePage - 1)}
          disabled={atFirst}
          className="px-3 py-1.5 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Prev
        </button>

        <span className="px-3 py-1.5 text-sm bg-gray-100 border rounded">
          Page {safePage} of {safeTotalPages}
        </span>

        <button
          onClick={() => onPageChange(safePage + 1)}
          disabled={atLast}
          className="px-3 py-1.5 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>

        <button
          onClick={() => onPageChange(safeTotalPages)}
          disabled={atLast}
          className="px-3 py-1.5 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Last
        </button>
      </div>
    </div>
  );
}
